import { UserRoundX } from '@/lib/icons';
import { ReportPageLayout } from '@/components/reports';
import { reportsService } from '@/services/reports-service';
import type { ColumnDef, FilterConfig, ReportFilters, AdminLevel } from '@/types/reports';
import { formatDisplayDateTime } from '@/lib/date-utils';

interface User {
  id: string;
  attributes: {
    affiliate_role: string;
    first_name: string;
    last_name: string;
    email: string;
    date_added?: string;
  };
}

const columns: ColumnDef<User>[] = [
  {
    key: 'affiliate_role',
    header: (adminLevel?: AdminLevel) =>
      adminLevel && ['super', 'admin'].includes(adminLevel) ? 'Affiliate Name' : 'User Role',
    render: (item) => item.attributes.affiliate_role,
  },
  {
    key: 'first_name',
    header: 'First Name',
    render: (item) => item.attributes.first_name,
  },
  {
    key: 'last_name',
    header: 'Last Name',
    render: (item) => item.attributes.last_name,
  },
  {
    key: 'email',
    header: 'Email',
    render: (item) => item.attributes.email,
  },
  {
    key: 'date_added',
    header: 'Date Added',
    render: (item) =>
      item.attributes.date_added
        ? formatDisplayDateTime(item.attributes.date_added)
        : '-',
  },
];

const filterConfig: FilterConfig = {
  fields: [
    { key: 'email_domain', type: 'text', label: 'Email Domain', placeholder: 'e.g., prosci.com' },
    { key: 'license_admin_id', type: 'organization-select', label: 'License Holder', placeholder: 'All License Holders', showFor: ['super', 'admin', 'affiliate_admin'] },
    { key: 'affiliate_id', type: 'affiliate-select', label: 'Affiliate', placeholder: 'All Affiliates', showFor: ['super', 'admin', 'affiliate_admin'] },
    { key: 'from_date', type: 'date', label: 'From Date', placeholder: 'Select date' },
    { key: 'to_date', type: 'date', label: 'To Date', placeholder: 'Select date' },
  ],
  hasFiltersCheck: (filters: ReportFilters) =>
    Boolean(
      filters.email_domain ||
      filters.from_date ||
      filters.to_date ||
      filters.affiliate_id ||
      filters.license_admin_id
    ),
};

export function UserListReport() {
  return (
    <ReportPageLayout<User>
      title="User List Report"
      description="Complete list of all portal users in your organization."
      breadcrumbLabel="User List"
      itemLabel="users"
      queryKey="user-list"
      fetchData={reportsService.getUserList}
      downloadData={reportsService.downloadUserList}
      filterConfig={filterConfig}
      columns={columns}
      sortData={(data) =>
        [...data].sort((a, b) => {
          const dateA = a.attributes.date_added ? new Date(a.attributes.date_added).getTime() : 0;
          const dateB = b.attributes.date_added ? new Date(b.attributes.date_added).getTime() : 0;
          return dateB - dateA;
        })
      }
      getRowKey={(item) => item.id}
      emptyIcon={<UserRoundX />}
      emptyTitle="No users found"
      emptyDescription="Try adjusting your filters to find users."
    />
  );
}
